import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import { COMPANY } from "@/lib/company";
import { AgiLogo } from "@/components/AgiLogo";
import { WindowDiagram } from "@/components/WindowDiagram";
import { AuthedImage } from "@/components/AuthedImage";
import { DrawingPicker } from "@/components/DrawingPicker";
import { TYPOLOGIES, computeItem, computeTotals, fmtMoney, fmtNum } from "@/lib/calc";
import { TYPOLOGY_DETAILS, sizeWarning } from "@/lib/typologyData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, Eye, FileSpreadsheet,
  Upload, X, Copy, GripVertical, AlertTriangle, RefreshCw, Lock, LockOpen,
} from "lucide-react";

const newItem = () => ({
  id: crypto.randomUUID(),
  code: "",
  typology: "Fixed",
  elevation: "",
  nos: 1,
  glass_type: "6mm Clear Toughened",
  glass_id: "g_clear_6",
  extra_glasses: [],
  remark: "",
  hide_annexure: false,
  fly_mesh: "",
  width_mm: "",
  height_mm: "",
  rate: "",
  rate_auto: true,
  rate_locked: false,
  margin_percent: 0,
  drawing_file_id: null,
  drawing_filename: "",
  notes: "",
});

const autoRate = (cfg, typology, glassId) => {
  if (!cfg) return "";
  const t = (cfg.typologies || []).find((x) => x.typology === typology);
  const g = (cfg.glasses || []).find((x) => x.id === glassId) || (cfg.glasses || [])[0];
  const alu = t ? t.alu_rate : 0;
  const glass = g ? g.glass_rate : 0;
  const total = alu + glass + (cfg.fabrication_per_sqft || 0) + (cfg.installation_per_sqft || 0) + (cfg.misc_per_sqft || 0);
  return Math.round(total * 100) / 100;
};

const rateBreakup = (cfg, typology, glassId) => {
  if (!cfg) return null;
  const t = (cfg.typologies || []).find((x) => x.typology === typology);
  const g = (cfg.glasses || []).find((x) => x.id === glassId) || (cfg.glasses || [])[0];
  return {
    alu: t ? t.alu_rate : 0,
    glass: g ? g.glass_rate : 0,
    fab: cfg.fabrication_per_sqft || 0,
    inst: cfg.installation_per_sqft || 0,
    misc: cfg.misc_per_sqft || 0,
  };
};

function EditableList({ items, onChange, testid, placeholder }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((val, i) => (
        <div key={i} className="flex gap-2 items-start">
          <span className="mt-2.5 text-xs font-mono text-slate-400 w-6 text-right shrink-0">
            {i + 1}.
          </span>
          <Textarea
            value={val}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            rows={2}
            className="rounded-sm text-sm resize-y min-h-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className="h-9 w-9 rounded-sm text-slate-400 hover:text-red-600 shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...items, ""])}
        className="rounded-sm ml-8"
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" /> {placeholder || "Add line"}
      </Button>
    </div>
  );
}

export default function QuotationBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [docId, setDocId] = useState(id || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [meta, setMeta] = useState({
    quote_no: "",
    quote_date: new Date().toISOString().slice(0, 10),
    client_name: "",
    client_location: "",
    project_title: "",
    subject: "",
    prepared_by: "",
    intro: "",
  });
  const [items, setItems] = useState([newItem()]);
  const [globalMargin, setGlobalMargin] = useState(0);
  const [gst, setGst] = useState(18);
  const [status, setStatus] = useState("draft");
  const [specs, setSpecs] = useState("");
  const [terms, setTerms] = useState([]);
  const [payment, setPayment] = useState([]);
  const [warranty, setWarranty] = useState([]);
  const [bankDetails, setBankDetails] = useState("");
  const [rateCfg, setRateCfg] = useState(null);
  const [picker, setPicker] = useState({ open: false, idx: null });
  const [syncing, setSyncing] = useState(false);
  const [rateStale, setRateStale] = useState(false);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: defaults }, { data: rateData }] = await Promise.all([
        api.get("/defaults"),
        api.get("/rates"),
      ]);
      setRateCfg(rateData);
      const resolveGlass = (i) =>
        i.glass_id || (rateData.glasses.find((g) => g.name === i.glass_type)?.id) || "g_clear_6";
      if (id) {
        const { data: q } = await api.get(`/quotations/${id}`);
        setDocId(q.id);
        setMeta({
          quote_no: q.quote_no || "",
          quote_date: q.quote_date || new Date().toISOString().slice(0, 10),
          client_name: q.client_name || "",
          client_location: q.client_location || "",
          project_title: q.project_title || "",
          subject: q.subject || defaults.subject,
          prepared_by: q.prepared_by || "",
          intro: q.intro || defaults.intro,
        });
        setItems(
          q.items && q.items.length
            ? q.items.map((i) => ({ ...newItem(), ...i, glass_id: resolveGlass(i), rate_auto: false }))
            : [{ ...newItem(), rate: autoRate(rateData, "Fixed", "g_clear_6"), rate_auto: true }]
        );
        if (q.items && q.items.length) {
          const loaded = q.items.map((i) => ({ ...newItem(), ...i, glass_id: resolveGlass(i) }));
          const stale = loaded.some(
            (it) =>
              !it.rate_locked &&
              Math.round(Number(autoRate(rateData, it.typology, it.glass_id))) !== Math.round(Number(it.rate))
          );
          setRateStale(stale);
        }
        setGlobalMargin(q.global_margin_percent || 0);
        setGst(q.gst_percent ?? 18);
        setStatus(q.status || "draft");
        setSpecs(q.technical_specs || defaults.technical_specs);
        setTerms(q.terms?.length ? q.terms : defaults.terms);
        setPayment(q.payment_terms?.length ? q.payment_terms : defaults.payment_terms);
        setWarranty(q.warranty?.length ? q.warranty : defaults.warranty);
        setBankDetails(q.bank_details || "");
      } else {
        setMeta((m) => ({ ...m, subject: defaults.subject, intro: defaults.intro, prepared_by: "AFSAL BABU K" }));
        setItems([{ ...newItem(), rate: autoRate(rateData, "Fixed", "g_clear_6"), rate_auto: true }]);
        setSpecs(defaults.technical_specs);
        setTerms(defaults.terms);
        setPayment(defaults.payment_terms);
        setWarranty(defaults.warranty);
        setBankDetails(rateData.bank_details || "");
      }
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const updateItem = (idx, field, value) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const changeTypology = (idx, v) =>
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const cust = (rateCfg?.typologies || []).find((x) => x.typology === v && x.custom);
        let glass_id = it.glass_id;
        let glass_type = it.glass_type;
        if (cust?.default_glass_id) {
          const g = (rateCfg?.glasses || []).find((x) => x.id === cust.default_glass_id);
          if (g) { glass_id = g.id; glass_type = g.name; }
        }
        return { ...it, typology: v, glass_id, glass_type, rate: it.rate_auto ? autoRate(rateCfg, v, glass_id) : it.rate };
      })
    );
  const changeGlass = (idx, glassId) =>
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const g = (rateCfg?.glasses || []).find((x) => x.id === glassId);
        return {
          ...it,
          glass_id: glassId,
          glass_type: g ? g.name : it.glass_type,
          rate: it.rate_auto ? autoRate(rateCfg, it.typology, glassId) : it.rate,
        };
      })
    );
  const changeRate = (idx, value) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, rate: value, rate_auto: false } : it)));
  const addExtraGlass = (idx) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, extra_glasses: [...(it.extra_glasses || []), ""] } : it)));
  const updateExtraGlass = (idx, gi, name) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, extra_glasses: (it.extra_glasses || []).map((g, j) => (j === gi ? name : g)) } : it)));
  const removeExtraGlass = (idx, gi) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, extra_glasses: (it.extra_glasses || []).filter((_, j) => j !== gi) } : it)));
  const toggleLock = (idx) =>
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, rate_locked: !it.rate_locked, rate_auto: it.rate_locked ? it.rate_auto : false } : it
      )
    );
  const addItem = () =>
    setItems((prev) => [
      ...prev,
      { ...newItem(), code: `W${String(prev.length + 1).padStart(2, "0")}`, rate: autoRate(rateCfg, "Fixed", "g_clear_6"), rate_auto: true },
    ]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const dupItem = (idx) =>
    setItems((prev) => {
      const copy = { ...prev[idx], id: crypto.randomUUID() };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });

  const attachDrawing = (fileId, name) => {
    const idx = picker.idx;
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, drawing_file_id: fileId, drawing_filename: name } : it))
    );
  };

  const buildPayload = () => ({
    ...meta,
    items: items.map((it) => ({
      ...it,
      extra_glasses: (it.extra_glasses || []).filter(Boolean),
      nos: Number(it.nos) || 0,
      width_mm: Number(it.width_mm) || 0,
      height_mm: Number(it.height_mm) || 0,
      rate: Number(it.rate) || 0,
      margin_percent: Number(it.margin_percent) || 0,
    })),
    global_margin_percent: Number(globalMargin) || 0,
    gst_percent: Number(gst) || 0,
    status,
    technical_specs: specs,
    terms,
    payment_terms: payment,
    warranty,
    bank_details: bankDetails,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let data;
      if (docId) {
        ({ data } = await api.put(`/quotations/${docId}`, payload));
      } else {
        ({ data } = await api.post("/quotations", payload));
        setDocId(data.id);
        window.history.replaceState(null, "", `/quotation/${data.id}/edit`);
      }
      setMeta((m) => ({ ...m, quote_no: data.quote_no, quote_date: data.quote_date }));
      if (payload.bank_details.trim()) setRateCfg((c) => ({ ...c, bank_details: payload.bank_details }));
      toast.success("Quotation saved");
      return data.id;
    } catch (e) {
      toast.error("Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndPreview = async () => {
    const savedId = await save();
    if (savedId) navigate(`/quotation/${savedId}/preview`);
  };

  const syncRates = async () => {
    setSyncing(true);
    try {
      const { data: fresh } = await api.get("/rates");
      setRateCfg(fresh);
      let changed = 0;
      const next = items.map((it) => {
        if (it.rate_locked) return it;
        const newRate = autoRate(fresh, it.typology, it.glass_id);
        if (Number(newRate) !== Number(it.rate)) changed += 1;
        return { ...it, rate: newRate, rate_auto: true };
      });
      setItems(next);
      setRateStale(false);
      const lockedCount = items.filter((it) => it.rate_locked).length;
      toast.success(
        changed > 0
          ? `Re-applied latest Library rates — ${changed} line${changed > 1 ? "s" : ""} updated${lockedCount ? `, ${lockedCount} locked line${lockedCount > 1 ? "s" : ""} kept` : ""}. Review and Save to keep.`
          : `All line rates already match the latest Library prices${lockedCount ? ` (${lockedCount} locked line${lockedCount > 1 ? "s" : ""} kept)` : ""}.`
      );
    } catch (e) {
      toast.error("Could not fetch the latest rates");
    } finally {
      setSyncing(false);
    }
  };

  const downloadExcel = async () => {
    const savedId = docId || (await save());
    if (!savedId) return;
    try {
      const res = await api.get(`/quotations/${savedId}/export/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(meta.quote_no || "quotation").replace(/\//g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  const totals = computeTotals(items, globalMargin, gst);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 blueprint-grid">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-button"
              onClick={() => navigate("/")}
              className="rounded-sm text-slate-500 shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AgiLogo size={30} />
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 leading-none truncate">
                {docId ? "Edit Quotation" : "New Quotation"}
              </div>
              <div className="text-xs text-slate-400 font-mono">{meta.quote_no || "auto-numbered"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="sync-rates-button"
              onClick={syncRates}
              disabled={syncing}
              className="rounded-sm border-blue-200 text-blue-700 hover:bg-blue-50"
              title="Re-apply the latest Rate Composition & Glass Library prices to every line in this quotation"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <RefreshCw className="w-4 h-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Sync rates</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="download-excel-button"
              onClick={downloadExcel}
              className="rounded-sm hidden sm:flex"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-600" /> Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="save-preview-button"
              onClick={saveAndPreview}
              className="rounded-sm"
            >
              <Eye className="w-4 h-4 mr-1.5" /> Preview / PDF
            </Button>
            <Button
              size="sm"
              data-testid="save-button"
              onClick={save}
              disabled={saving}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6 min-w-0">
          {rateStale && (
            <div data-testid="rate-stale-banner" className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <div className="text-sm text-amber-800 min-w-0">
                <b>Library prices changed</b> since this quote was saved. Some line rates differ from the current Rate Composition / Glass Library.
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  size="sm"
                  data-testid="rate-stale-sync-button"
                  onClick={syncRates}
                  disabled={syncing}
                  className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                  Sync now
                </Button>
                <button
                  type="button"
                  data-testid="rate-stale-dismiss"
                  onClick={() => setRateStale(false)}
                  className="text-amber-500 hover:text-amber-700 p-1"
                  title="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {/* Client / project card */}
          <section className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Project & Client
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Project Title">
                <Input
                  data-testid="project-title-input"
                  value={meta.project_title}
                  onChange={(e) => setMeta({ ...meta, project_title: e.target.value })}
                  placeholder="Proposed Residence for ..."
                  className="rounded-sm h-9"
                />
              </Field>
              <Field label="Client Name">
                <Input
                  data-testid="client-name-input"
                  value={meta.client_name}
                  onChange={(e) => setMeta({ ...meta, client_name: e.target.value })}
                  placeholder="Mr. Vinoth Kumar"
                  className="rounded-sm h-9"
                />
              </Field>
              <Field label="Client Location">
                <Input
                  data-testid="client-location-input"
                  value={meta.client_location}
                  onChange={(e) => setMeta({ ...meta, client_location: e.target.value })}
                  placeholder="Coimbatore"
                  className="rounded-sm h-9"
                />
              </Field>
              <Field label="Prepared By">
                <Input
                  value={meta.prepared_by}
                  onChange={(e) => setMeta({ ...meta, prepared_by: e.target.value })}
                  className="rounded-sm h-9"
                />
              </Field>
              <Field label="Quote No (auto if blank)">
                <Input
                  value={meta.quote_no}
                  onChange={(e) => setMeta({ ...meta, quote_no: e.target.value })}
                  className="rounded-sm h-9 font-mono"
                  placeholder={COMPANY.name}
                />
              </Field>
              <Field label="Date">
                <Input
                  type="date"
                  value={meta.quote_date}
                  onChange={(e) => setMeta({ ...meta, quote_date: e.target.value })}
                  className="rounded-sm h-9"
                />
              </Field>
              <Field label="Status">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select" className="h-9 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Subject">
                  <Input
                    value={meta.subject}
                    onChange={(e) => setMeta({ ...meta, subject: e.target.value })}
                    className="rounded-sm h-9"
                  />
                </Field>
              </div>
            </div>
          </section>

          {/* BOQ items */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Bill of Quantities — {items.length} item{items.length !== 1 && "s"}
              </h2>
              <Button
                size="sm"
                data-testid="add-item-button"
                onClick={addItem}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add Item
              </Button>
            </div>

            <div className="space-y-4">
              {items.map((it, idx) => {
                const c = computeItem(it);
                return (
                  <motion.div
                    key={it.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`item-card-${idx}`}
                    className="bg-white border border-slate-200 rounded-sm overflow-hidden avoid-break"
                  >
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                      <GripVertical className="w-4 h-4 text-slate-300" />
                      <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                      <Input
                        data-testid={`item-code-${idx}`}
                        value={it.code}
                        onChange={(e) => updateItem(idx, "code", e.target.value)}
                        placeholder="W01"
                        className="rounded-sm h-8 w-24 font-mono font-semibold"
                      />
                      <div className="flex-1" />
                      <span className="text-sm font-mono font-semibold text-slate-900">
                        ₹ {fmtMoney(c.amount)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => dupItem(idx)}
                        className="h-8 w-8 rounded-sm text-slate-400 hover:text-slate-700"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`remove-item-${idx}`}
                        onClick={() => removeItem(idx)}
                        className="h-8 w-8 rounded-sm text-slate-400 hover:text-red-600"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_230px] gap-4 p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="Typology" className="col-span-2">
                          <Select value={it.typology} onValueChange={(v) => changeTypology(idx, v)}>
                            <SelectTrigger data-testid={`item-typology-${idx}`} className="h-9 rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(() => {
                                const all = rateCfg?.typologies?.length ? rateCfg.typologies : null;
                                if (!all) {
                                  return TYPOLOGIES.map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ));
                                }
                                const builtin = all.filter((t) => !t.custom);
                                const custom = all.filter((t) => t.custom);
                                return (
                                  <>
                                    <SelectGroup>
                                      <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Built-in — IWIN-S</SelectLabel>
                                      {builtin.map((t) => (
                                        <SelectItem key={t.typology} value={t.typology}>{t.typology}</SelectItem>
                                      ))}
                                    </SelectGroup>
                                    {custom.length > 0 && (
                                      <SelectGroup>
                                        <SelectLabel className="text-[10px] uppercase tracking-wider text-blue-500">Custom / Imported</SelectLabel>
                                        {custom.map((t) => (
                                          <SelectItem key={t.typology} value={t.typology}>{t.typology}</SelectItem>
                                        ))}
                                      </SelectGroup>
                                    )}
                                  </>
                                );
                              })()}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Elevation">
                          <Input
                            value={it.elevation}
                            onChange={(e) => updateItem(idx, "elevation", e.target.value)}
                            placeholder="SD1"
                            className="rounded-sm h-9"
                          />
                        </Field>
                        <Field label="Nos">
                          <Input
                            type="number"
                            min="0"
                            data-testid={`item-nos-${idx}`}
                            value={it.nos}
                            onChange={(e) => updateItem(idx, "nos", e.target.value)}
                            className="rounded-sm h-9 font-mono"
                          />
                        </Field>
                        <Field label="Width (mm)">
                          <Input
                            type="number"
                            min="0"
                            data-testid={`item-width-${idx}`}
                            value={it.width_mm}
                            onChange={(e) => updateItem(idx, "width_mm", e.target.value)}
                            className="rounded-sm h-9 font-mono"
                          />
                        </Field>
                        <Field label="Height (mm)">
                          <Input
                            type="number"
                            min="0"
                            data-testid={`item-height-${idx}`}
                            value={it.height_mm}
                            onChange={(e) => updateItem(idx, "height_mm", e.target.value)}
                            className="rounded-sm h-9 font-mono"
                          />
                        </Field>
                        <Field label="Glass Type" className="col-span-2">
                          <Select value={it.glass_id || ""} onValueChange={(v) => changeGlass(idx, v)}>
                            <SelectTrigger data-testid={`item-glass-${idx}`} className="h-9 rounded-sm">
                              <SelectValue placeholder="Select glass" />
                            </SelectTrigger>
                            <SelectContent>
                              {(rateCfg?.glasses || []).map((g) => (
                                <SelectItem key={g.id} value={g.id}>{g.name} — ₹{g.glass_rate}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(it.extra_glasses || []).map((gName, gi) => (
                            <div key={gi} className="flex items-center gap-1 mt-1.5">
                              <Select value={gName || ""} onValueChange={(v) => updateExtraGlass(idx, gi, v)}>
                                <SelectTrigger data-testid={`item-extra-glass-${idx}-${gi}`} className="h-9 rounded-sm">
                                  <SelectValue placeholder="Additional glass" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(rateCfg?.glasses || []).map((g) => (
                                    <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <button
                                type="button"
                                data-testid={`item-extra-glass-remove-${idx}-${gi}`}
                                onClick={() => removeExtraGlass(idx, gi)}
                                className="shrink-0 h-9 w-9 flex items-center justify-center rounded-sm border border-slate-200 text-slate-400 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            data-testid={`item-add-glass-${idx}`}
                            onClick={() => addExtraGlass(idx)}
                            className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700"
                          >
                            <Plus className="w-3 h-3" /> Add glass type
                          </button>
                        </Field>
                        <Field label="Fly Mesh">
                          <Select
                            value={it.fly_mesh || "none"}
                            onValueChange={(v) => updateItem(idx, "fly_mesh", v === "none" ? "" : v)}
                          >
                            <SelectTrigger className="h-9 rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">—</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label={it.rate_locked ? "Rate (/Sqft) · Locked" : "Rate (/Sqft)"}>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              data-testid={`item-rate-${idx}`}
                              value={it.rate}
                              onChange={(e) => changeRate(idx, e.target.value)}
                              className={`rounded-sm h-9 font-mono ${it.rate_locked ? "border-amber-300 bg-amber-50" : ""}`}
                            />
                            <button
                              type="button"
                              data-testid={`item-rate-lock-${idx}`}
                              onClick={() => toggleLock(idx)}
                              title={it.rate_locked
                                ? "Rate locked — Sync will skip this line. Click to unlock."
                                : "Lock this rate so Sync won't change it"}
                              className={`shrink-0 h-9 w-9 flex items-center justify-center rounded-sm border transition-colors ${it.rate_locked ? "border-amber-300 bg-amber-100 text-amber-700" : "border-slate-200 text-slate-400 hover:text-slate-600"}`}
                            >
                              {it.rate_locked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                            </button>
                          </div>
                        </Field>
                        <Field label="Margin % (line)">
                          <Input
                            type="number"
                            data-testid={`item-margin-${idx}`}
                            value={it.margin_percent}
                            onChange={(e) => updateItem(idx, "margin_percent", e.target.value)}
                            className="rounded-sm h-9 font-mono"
                          />
                        </Field>
                        <Field label="Remark (shown in BOQ)" className="col-span-2 md:col-span-4">
                          <Input
                            data-testid={`item-remark-${idx}`}
                            value={it.remark || ""}
                            onChange={(e) => updateItem(idx, "remark", e.target.value)}
                            placeholder="e.g. Toughened outer / laminated inner; site-measured"
                            className="rounded-sm h-9"
                          />
                        </Field>

                        <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs font-mono text-slate-500 border-t border-slate-100 mt-1">
                          <span>Qty: <b className="text-slate-800">{fmtNum(c.qty_sqft)}</b> sqft</span>
                          <span>Basic: <b className="text-slate-800">₹ {fmtMoney(c.basic_amount)}</b></span>
                          {Number(it.margin_percent) !== 0 && (
                            <span>+{it.margin_percent}% margin</span>
                          )}
                          <button
                            type="button"
                            data-testid={`item-attach-${idx}`}
                            onClick={() => setPicker({ open: true, idx })}
                            className="ml-auto inline-flex items-center gap-1.5 cursor-pointer text-blue-600 hover:text-blue-700"
                          >
                            <Upload className="w-3.5 h-3.5" /> Attach drawing
                          </button>
                          {it.rate_auto && rateCfg && (() => {
                            const b = rateBreakup(rateCfg, it.typology, it.glass_id);
                            return (
                              <span data-testid={`rate-breakup-${idx}`} className="w-full text-[10px] text-slate-400">
                                Rate build-up: Alu ₹{b.alu} + Glass ₹{b.glass} + Fab ₹{b.fab} + Install ₹{b.inst} + Misc ₹{b.misc} = <b className="text-slate-600">₹{fmtMoney(Number(it.rate))}</b>/sqft
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      {/* live diagram / uploaded reference elevation + spec */}
                      <div className="flex flex-col gap-2">
                        <div className="border border-slate-200 rounded-sm bg-white p-2 h-[150px]" data-testid={`item-elevation-${idx}`}>
                          {(() => {
                            const cust = rateCfg && (rateCfg.typologies || []).find((x) => x.typology === it.typology && x.custom);
                            return cust?.drawing_file_id ? (
                              <AuthedImage
                                fileId={cust.drawing_file_id}
                                filename={cust.drawing_filename}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <WindowDiagram
                                typology={it.typology}
                                width={it.width_mm}
                                height={it.height_mm}
                                code={it.code}
                                elevation={it.elevation}
                                className="w-full h-full"
                              />
                            );
                          })()}
                        </div>
                        {(() => {
                          const d = TYPOLOGY_DETAILS[it.typology];
                          const cust = !d && rateCfg ? (rateCfg.typologies || []).find((x) => x.typology === it.typology && x.custom) : null;
                          const warn = sizeWarning(it.typology, it.width_mm, it.height_mm);
                          return (
                            <div className="text-[9px] leading-snug text-slate-500 space-y-0.5" data-testid={`item-spec-${idx}`}>
                              {d && <div className="font-semibold text-slate-600">{d.system}</div>}
                              {d && <div>Frame {d.frame_depth} · {d.glazing}</div>}
                              {d?.size && <div>Range: {d.size}</div>}
                              {d?.hardware && <div>Hardware: {d.hardware}</div>}
                              {cust && <div className="font-semibold text-slate-600">Custom Typology</div>}
                              {cust && (cust.frame_depth || cust.glazing) && (
                                <div>
                                  {cust.frame_depth ? `Frame ${cust.frame_depth}` : ""}
                                  {cust.frame_depth && cust.glazing ? " · " : ""}
                                  {cust.glazing}
                                </div>
                              )}
                              {cust?.hardware && <div>Hardware: {cust.hardware}</div>}
                              {warn && (
                                <div className="text-red-600 flex items-center gap-1" data-testid={`item-sizewarn-${idx}`}>
                                  <AlertTriangle className="w-3 h-3 shrink-0" />{warn}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {it.drawing_file_id && (
                          <>
                            <div className="relative border border-slate-200 rounded-sm overflow-hidden h-[90px] bg-slate-50">
                              <AuthedImage
                                fileId={it.drawing_file_id}
                                filename={it.drawing_filename}
                                className="w-full h-full object-contain"
                              />
                              <button
                                onClick={() => {
                                  updateItem(idx, "drawing_file_id", null);
                                  updateItem(idx, "drawing_filename", "");
                                }}
                                className="absolute top-1 right-1 bg-white/90 border border-slate-200 rounded-sm p-0.5 text-slate-500 hover:text-red-600"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <label
                              className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer select-none"
                              data-testid={`item-annexure-toggle-${idx}`}
                            >
                              <input
                                type="checkbox"
                                checked={!it.hide_annexure}
                                onChange={(e) => updateItem(idx, "hide_annexure", !e.target.checked)}
                                className="rounded-sm accent-blue-600"
                              />
                              Include as annexure page in export
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Specs / Terms / Payment / Warranty */}
          <section className="bg-white border border-slate-200 rounded-sm p-5">
            <Tabs defaultValue="specs">
              <TabsList className="rounded-sm bg-slate-100 flex-wrap h-auto">
                <TabsTrigger value="specs" data-testid="tab-specs" className="rounded-sm">Technical Specs</TabsTrigger>
                <TabsTrigger value="warranty" className="rounded-sm">Warranty</TabsTrigger>
                <TabsTrigger value="payment" className="rounded-sm">Payment Terms</TabsTrigger>
                <TabsTrigger value="bank" data-testid="tab-bank" className="rounded-sm">Bank Details</TabsTrigger>
                <TabsTrigger value="terms" data-testid="tab-terms" className="rounded-sm">Terms & Conditions</TabsTrigger>
              </TabsList>
              <TabsContent value="specs" className="pt-4">
                <Textarea
                  data-testid="specs-textarea"
                  value={specs}
                  onChange={(e) => setSpecs(e.target.value)}
                  rows={16}
                  className="rounded-sm text-sm font-mono leading-relaxed"
                />
              </TabsContent>
              <TabsContent value="warranty" className="pt-4">
                <EditableList items={warranty} onChange={setWarranty} testid="warranty-list" placeholder="Add warranty line" />
              </TabsContent>
              <TabsContent value="payment" className="pt-4">
                <EditableList items={payment} onChange={setPayment} testid="payment-list" placeholder="Add payment stage" />
              </TabsContent>
              <TabsContent value="bank" className="pt-4">
                <Textarea
                  data-testid="bank-details-textarea"
                  value={bankDetails}
                  onChange={(e) => setBankDetails(e.target.value)}
                  rows={6}
                  placeholder={"Account Name: \nBank: \nAccount No.: \nBranch: \nIFSC: "}
                  className="rounded-sm text-sm leading-relaxed"
                />
                {rateCfg?.bank_details && rateCfg.bank_details !== bankDetails && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="bank-insert-saved"
                    onClick={() => setBankDetails(rateCfg.bank_details)}
                    className="rounded-sm mt-2"
                  >
                    Use saved bank details
                  </Button>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Appears after Payment Terms on the PDF &amp; Excel. Saved for future quotes once you save this one.
                </p>
              </TabsContent>
              <TabsContent value="terms" className="pt-4">
                <EditableList items={terms} onChange={setTerms} testid="terms-list" placeholder="Add term" />
              </TabsContent>
            </Tabs>
          </section>
        </div>

        {/* Sticky summary */}
        <aside className="xl:sticky xl:top-[88px] h-fit">
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Commercial Summary
            </h2>

            <div className="space-y-3 mb-4">
              <Field label="Margin on Basic (%)">
                <Input
                  type="number"
                  data-testid="global-margin-input"
                  value={globalMargin}
                  onChange={(e) => setGlobalMargin(e.target.value)}
                  className="rounded-sm h-9 font-mono"
                />
              </Field>
              <Field label="GST (%)">
                <Input
                  type="number"
                  data-testid="gst-input"
                  value={gst}
                  onChange={(e) => setGst(e.target.value)}
                  className="rounded-sm h-9 font-mono"
                />
              </Field>
            </div>

            <Separator className="my-4" />

            <dl className="space-y-2.5 text-sm font-mono">
              <Row l="Basic Amount" v={totals.basicSubtotal} />
              {totals.lineMarginTotal !== 0 && <Row l="Line Margin" v={totals.lineMarginTotal} muted />}
              {totals.globalMarginAmount !== 0 && (
                <Row l={`Margin @ ${totals.globalMarginPercent}%`} v={totals.globalMarginAmount} muted />
              )}
              <div className="flex justify-between pt-1 border-t border-slate-100">
                <dt className="text-slate-600">Sub Total</dt>
                <dd className="font-semibold text-slate-900" data-testid="summary-taxable">
                  ₹ {fmtMoney(totals.taxable)}
                </dd>
              </div>
              <Row l={`GST @ ${totals.gstPercent}%`} v={totals.gstAmount} muted />
            </dl>

            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-sm px-4 py-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-blue-700 font-semibold">Grand Total</span>
              <span className="font-display text-xl font-bold text-blue-700" data-testid="summary-grand-total">
                ₹ {fmtMoney(totals.grandTotal)}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-center">
              <div className="bg-slate-50 rounded-sm py-2">
                <div className="text-lg font-bold font-mono text-slate-900">{totals.totalUnits}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Units</div>
              </div>
              <div className="bg-slate-50 rounded-sm py-2">
                <div className="text-lg font-bold font-mono text-slate-900">{fmtNum(totals.totalSqft)}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Sqft</div>
              </div>
            </div>

            <Button
              onClick={saveAndPreview}
              className="w-full mt-4 rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              <Eye className="w-4 h-4 mr-2" /> Generate Quotation
            </Button>
          </div>
        </aside>
      </main>

      <DrawingPicker
        open={picker.open}
        onOpenChange={(o) => setPicker((p) => ({ ...p, open: o }))}
        defaultTypology={picker.idx != null ? items[picker.idx]?.typology || "Fixed" : "Fixed"}
        onSelect={attachDrawing}
      />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-400">{label}</Label>
      {children}
    </div>
  );
}

function Row({ l, v, muted }) {
  return (
    <div className="flex justify-between">
      <dt className={muted ? "text-slate-500" : "text-slate-600"}>{l}</dt>
      <dd className={muted ? "text-slate-600" : "text-slate-800 font-medium"}>₹ {fmtMoney(v)}</dd>
    </div>
  );
}
